import React from "react";
import { Box, Grid, Button } from "@mui/material";
import { WhatsApp as WhatsAppIcon } from "@mui/icons-material";
import { PageContainer } from "../../components/container/PageContainer";
import LoadingDialogWithMessage from "../../components/Dialogs/LoadingDialogWithMessage";
import { RenderUpsellServiceVenueCard } from "./components/RenderUpsellServiceVenueCard";
import { PageTitleBar } from "../../components/PageTitleBar/PageTitleBar";
import { UpsellLeadsRightPanel } from "./components/UpsellLeadsRightPanel";
import { NoRecordFound } from "../../components/NoRecordFound/NoRecordFound";
import ConfirmationCampaign from "./components/dashboard/ConfirmationCampaign";

import {
  BookingPeriodCard,
  NoBookedLeadsWarning,
  TopPerformers,
  UnableToFetchInsightsHero,
  VenuePerformanceDonutGraph,
  ServiceCategoriesDonutGraph,
  FoodPerformanceDonutGraph,
} from "./components";
import { BOOKING_PERIOD_OPTIONS } from "./constants/dashboardConstants";
import { useDispatchWrapper } from "../../hooks";
import { useUpsellDashboard } from "./hooks/useUpsellDashboard";
import { useUpsellCardSelection } from "./hooks/useUpsellCardSelection";
import { useUpsellMetrics } from "./hooks/useUpsellMetrics";

export const UpsellDashboard: React.FC = () => {
  const dispatch = useDispatchWrapper();
  // Custom hooks for state management
  const {
    dashboardData,
    showUnableUpsell,
    showWarning,
    selectedBookingPeriod,
    selectedServiceCategory,
    selectedVenueCategory,
    selectedFoodCategory,
    confirmDialogOpen,
    campaignLoading,
    serviceCardsWithUpsells,
    venueCardsWithUpsells,
    foodCardsWithUpsells,
    setSelectedServiceCategory,
    setSelectedVenueCategory,
    setSelectedFoodCategory,
    setConfirmDialogOpen,
    handleBookingPeriodSelect,
    confirmCampaignGeneration,
    initiateCampaignGeneration,
    navigate,
  } = useUpsellDashboard();

  // Card selection hook
  const {
    selectedCount,
    handleCardCheckChange,
    isCardSelected,
    getSelectedUuids,
    getSelectedCardNames,
    clearSelection,
    selectAll,
  } = useUpsellCardSelection();

  // Metrics calculation hook
  const metrics = useUpsellMetrics(dashboardData);

  // Handle bulk auto generate campaign
  const handleBulkAutoGenerateCampaign = () => {
    const selectedUuids = getSelectedUuids();
    initiateCampaignGeneration(selectedUuids, selectedCount);
  };

  // Calculate total cards count
  const totalCardsCount =
    (venueCardsWithUpsells?.length || 0) +
    (serviceCardsWithUpsells?.length || 0) +
    (foodCardsWithUpsells?.length || 0);

  // Check if all cards are selected
  const allCardsSelected =
    totalCardsCount > 0 && selectedCount === totalCardsCount;

  // Handle select all cards
  const handleSelectAllCards = () => {
    if (allCardsSelected) {
      // If all are selected, deselect all
      clearSelection();
      return;
    }

    const allCards: {
      uuid: string;
      type: "venue" | "service" | "food";
      name?: string;
    }[] = [];

    // Add all venue cards
    if (venueCardsWithUpsells && venueCardsWithUpsells.length > 0) {
      venueCardsWithUpsells.forEach((item: any) => {
        if (item.venue_uuid) {
          allCards.push({
            uuid: item.venue_uuid,
            type: "venue",
            name: item.venue_name || "",
          });
        }
      });
    }

    // Add all service cards
    if (serviceCardsWithUpsells && serviceCardsWithUpsells.length > 0) {
      serviceCardsWithUpsells.forEach((item: any) => {
        if (item.service_category_uuid) {
          allCards.push({
            uuid: item.service_category_uuid,
            type: "service",
            name: item.service_category_name || "",
          });
        }
      });
    }

    // Add all food cards
    if (foodCardsWithUpsells && foodCardsWithUpsells.length > 0) {
      foodCardsWithUpsells.forEach((item: any) => {
        if (item.food_item_uuid) {
          allCards.push({
            uuid: item.food_item_uuid,
            type: "food",
            name: item.item_name || "",
          });
        }
      });
    }

    selectAll(allCards);
  };

  // Handle campaign confirmation with clear selection
  const handleConfirmCampaign = async () => {
    const success = await confirmCampaignGeneration();
    if (success) {
      clearSelection();
    }
  };

  return (
    <PageContainer
      title="Upselling Dashboard"
      description="Monitor and analyze upselling performance"
    >
      <Box sx={{ p: 3, pt: 0 }}>
        <Grid item xs={12} md={6}>
          {!showUnableUpsell && (
            <PageTitleBar
              heading="Welcome to Upselling Dashboard"
              sx={{
                "& .MuiTypography-h2": {
                  whiteSpace: { xs: "nowrap", md: "normal" },
                  overflow: { xs: "hidden", md: "visible" },
                  textOverflow: { xs: "ellipsis", md: "clip" },
                  fontSize: { xs: "1.1rem", md: "1.5rem" },
                },
              }}
              rightHeading={
                showUnableUpsell ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      showUnableUpsell
                        ? navigate("/packages/manage")
                        : navigate("/event-leads/manage");
                    }}
                  >
                    Create Package
                  </Button>
                ) : (
                  <></>
                )
              }
            />
          )}

          {showWarning && <NoBookedLeadsWarning />}
        </Grid>

        {showUnableUpsell ? (
          <UnableToFetchInsightsHero />
        ) : (
          <>
            {/* Booking Period Selection */}
            <Grid container spacing={3} mb={3}>
              {BOOKING_PERIOD_OPTIONS.map((period) => (
                <Grid item xs={6} sm={4} md={2} key={period.value}>
                  <BookingPeriodCard
                    label={period.label}
                    subLabel={period.subLabel}
                    value={period.value}
                    isSelected={selectedBookingPeriod === period.value}
                    onSelect={handleBookingPeriodSelect}
                  />
                </Grid>
              ))}
            </Grid>

            <PageTitleBar
              heading="Recent Purchases"
              sx={{
                "& .page-title-bar-inner": {
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: { xs: "flex-start", md: "flex-start" },
                  gap: { xs: 1, md: 0 },
                },
                "& .MuiTypography-h2": {
                  whiteSpace: { xs: "nowrap", md: "normal" },
                  overflow: { xs: "hidden", md: "visible" },
                  textOverflow: { xs: "ellipsis", md: "clip" },
                  fontSize: { xs: "1.1rem", md: "1.5rem" },
                },
              }}
              rightHeading={
                <Box
                  sx={{
                    display: "flex",
                    gap: { xs: 1, md: 2 },
                    mt: { xs: 0.5, md: 0 },
                    "& .MuiButton-root": {
                      whiteSpace: "nowrap",
                      fontSize: { xs: "0.7rem", md: "0.875rem" },
                      px: { xs: 1.5, md: 3 },
                      py: { xs: 0.5, md: 1 },
                      minWidth: { xs: "auto", md: 64 },
                    },
                  }}
                >
                  <Button
                    variant={allCardsSelected ? "outlined" : "contained"}
                    color="primary"
                    onClick={handleSelectAllCards}
                    disabled={
                      !venueCardsWithUpsells?.length &&
                      !serviceCardsWithUpsells?.length &&
                      !foodCardsWithUpsells?.length
                    }
                  >
                    {allCardsSelected ? "Deselect All" : "Select All Cards"}
                  </Button>

                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<WhatsAppIcon />}
                    onClick={handleBulkAutoGenerateCampaign}
                    disabled={selectedCount === 0}
                  >
                    {selectedCount > 0
                      ? `Auto Generate Campaign (${selectedCount})`
                      : "Auto Generate Campaign"}
                  </Button>
                </Box>
              }
            />

            {/* Upsell Cards Section */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              {/* Venue Cards */}
              {venueCardsWithUpsells &&
                venueCardsWithUpsells.length > 0 &&
                venueCardsWithUpsells.map((item: any, index: number) => (
                  <RenderUpsellServiceVenueCard
                    key={`venue-${index}`}
                    venue={item}
                    isFromUpsellDashboard
                    isSelected={isCardSelected(item.venue_uuid)}
                    upsellData={item.uniqueUpsells}
                    isSelectable
                    isChecked={isCardSelected(item.venue_uuid)}
                    onCheckChange={handleCardCheckChange}
                    onViewLeads={() => {
                      if ((item.leads?.length ?? 0) > 0) {
                        setSelectedVenueCategory(item);
                      }
                    }}
                  />
                ))}

              {/* Service Cards */}
              {serviceCardsWithUpsells &&
                serviceCardsWithUpsells.length > 0 &&
                serviceCardsWithUpsells.map((item: any, index: number) => (
                  <RenderUpsellServiceVenueCard
                    key={`service-${index}`}
                    service={item}
                    isFromUpsellDashboard
                    isSelected={isCardSelected(item.service_category_uuid)}
                    upsellData={item.uniqueUpsells}
                    isSelectable
                    isChecked={isCardSelected(item.service_category_uuid)}
                    onCheckChange={handleCardCheckChange}
                    onViewLeads={() => {
                      if ((item.leads?.length ?? 0) > 0) {
                        setSelectedServiceCategory(item);
                      }
                    }}
                  />
                ))}

              {/* Food Cards */}
              {foodCardsWithUpsells &&
                foodCardsWithUpsells.length > 0 &&
                foodCardsWithUpsells.map((item: any, index: number) => (
                  <RenderUpsellServiceVenueCard
                    key={`food-${index}`}
                    food={item}
                    isFromUpsellDashboard
                    isSelected={isCardSelected(item.food_item_uuid)}
                    upsellData={item.uniqueUpsells}
                    isSelectable
                    isChecked={isCardSelected(item.food_item_uuid)}
                    onCheckChange={handleCardCheckChange}
                    onViewLeads={() => {
                      if ((item.leads?.length ?? 0) > 0) {
                        setSelectedFoodCategory(item);
                      }
                    }}
                  />
                ))}

              {/* No Records Found */}
              {!serviceCardsWithUpsells?.length &&
                !venueCardsWithUpsells?.length &&
                !foodCardsWithUpsells?.length && (
                  <NoRecordFound
                    title="No upsell opportunities found"
                    description="No upsell opportunities found"
                    showAddButton={false}
                    showRefreshButton={false}
                    showFilterButton={false}
                    height="100px"
                  />
                )}
            </Box>

            {/* Analytics Section */}
            <Box mt={5} mb={3}>
              <PageTitleBar heading="Analytics Overview" />
            </Box>

            <Grid container spacing={3} mt={1} mb={3}>
              <Grid item xs={12} md={4}>
                <ServiceCategoriesDonutGraph dashboardData={dashboardData} />
              </Grid>

              <Grid item xs={12} md={4}>
                <VenuePerformanceDonutGraph dashboardData={dashboardData} />
              </Grid>

              <Grid item xs={12} md={4}>
                <FoodPerformanceDonutGraph dashboardData={dashboardData} />
              </Grid>
            </Grid>

            {/* Top Performers */}
            <Box sx={{ mb: 4 }}>
              <TopPerformers dashboardData={dashboardData} metrics={metrics} />
            </Box>
          </>
        )}
      </Box>

      {/* Confirmation Dialog for Auto Generate Campaign */}
      <ConfirmationCampaign
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        selectedCards={getSelectedCardNames()}
        onConfirm={handleConfirmCampaign}
      />

      {/* Loading Dialog for Campaign Generation */}
      {campaignLoading && (
        <LoadingDialogWithMessage
          open={campaignLoading}
          label="Generating campaign..."
        />
      )}

      {/* Venue Category Right Panel */}
      {selectedVenueCategory && (
        <UpsellLeadsRightPanel
          open={Boolean(selectedVenueCategory)}
          onClose={() => setSelectedVenueCategory(null)}
          selectedVenueCategory={selectedVenueCategory}
          setSelectedVenueCategory={setSelectedVenueCategory}
          selectedBookingPeriod={selectedBookingPeriod}
        />
      )}

      {/* Service Category Right Panel */}
      {selectedServiceCategory && (
        <UpsellLeadsRightPanel
          open={Boolean(selectedServiceCategory)}
          onClose={() => setSelectedServiceCategory(null)}
          selectedVenueCategory={selectedServiceCategory}
          setSelectedVenueCategory={setSelectedServiceCategory}
          selectedBookingPeriod={selectedBookingPeriod}
        />
      )}

      {/*Food Item Right Panel */}
      {selectedFoodCategory && (
        <UpsellLeadsRightPanel
          open={Boolean(selectedFoodCategory)}
          onClose={() => setSelectedFoodCategory(null)}
          selectedVenueCategory={selectedFoodCategory}
          setSelectedVenueCategory={setSelectedFoodCategory}
          selectedBookingPeriod={selectedBookingPeriod}
        />
      )}
    </PageContainer>
  );
};
